import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { initTelemetry, telemetry } from './app/core/telemetry/telemetry';
import { initFaro } from './app/core/telemetry/faro';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

// Faro first: it reports into the platform's own observability stack, where the
// service metrics and logs already live. OpenObserve stays as a fallback but is
// inert in production today — no client token is baked into the prod build.
// Never both: two RUM SDKs mean two beacon streams and two places to look.
if (environment.faro?.url) {
  // Fire-and-forget — telemetry must not delay the first paint, and a collector
  // that is down must not block the admin from loading.
  void initFaro({
    url: environment.faro.url,
    appName: 'admin',
    appVersion: `${environment.appVersion}-${environment.buildNumber}`,
    environment: environment.name,
    sampleRate: environment.faro.sampleRate
  });
} else if (environment.openobserve?.enabled && environment.openobserve?.clientToken) {
  initTelemetry({
    enabled: environment.openobserve.enabled,
    clientToken: environment.openobserve.clientToken,
    applicationId: environment.openobserve.applicationId,
    site: environment.openobserve.site,
    organizationIdentifier: environment.openobserve.organizationIdentifier,
    service: environment.openobserve.service,
    env: environment.openobserve.environment,
    // OpenObserve tags reject '+', so use '-' between version and build number.
    version: `${environment.appVersion}-${environment.buildNumber}`,
    apiVersion: environment.openobserve.apiVersion,
    insecureHTTP: environment.openobserve.insecureHTTP,
    sessionSampleRate: environment.openobserve.sessionSampleRate,
    sessionReplaySampleRate: environment.openobserve.sessionReplaySampleRate,
    defaultPrivacyLevel: environment.openobserve.defaultPrivacyLevel,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true
    // NOTE: distributed tracing (allowedTracingUrls) is intentionally OFF.
    // Enabling it injects `traceparent`/`tracestate` headers into API requests,
    // which triggers CORS preflights that every backend service must allow
    // (Access-Control-Allow-Headers: traceparent, tracestate). Re-enable only
    // after the API gateway + all microservices permit those headers:
    //   allowedTracingUrls: [environment.apiBaseUrl]
  });

  // Surface lazy-chunk failures explicitly — these usually mean the user is on a
  // stale build after a deploy and need a hard reload.
  window.addEventListener('error', (event) => {
    const err = event?.error;
    const message = err?.message || event?.message || '';
    if (
      err?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(message)
    ) {
      telemetry.captureException(err ?? new Error(message), {
        tags: { area: 'chunk_load', stale_build: 'likely' }
      });
    }
  });
}

platformBrowserDynamic()
  .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
  .catch((err) => console.error(err));
